import { useTranslation } from 'react-i18next';

import { displayToastError } from '@/helpers/display-toast';

import { ApiError, BACKEND_ERROR_CODE_TRANSLATIONS } from '../api/errors';

export const useApiError = () => {
  const { t } = useTranslation();

  const resolveBackendCodeMessage = (
    backendCode?: number,
    backendArgs?: string[]
  ): string | undefined => {
    if (backendCode === undefined) return undefined;
    const translationKey = BACKEND_ERROR_CODE_TRANSLATIONS[backendCode];
    if (!translationKey) return undefined;
    let translated = t(translationKey, { defaultValue: '' });
    if (!translated) return undefined;
    // Replace positional placeholders ($1, $2, ...) with backend args
    backendArgs?.forEach((arg, index) => {
      translated = translated.replace(`$${index + 1}`, arg);
    });
    return translated;
  };

  const getErrorMessage = (error: unknown, preferBackendMessage = false): string => {
    if (error instanceof ApiError) {
      // If preferBackendMessage is true and backend message exists, use it
      if (preferBackendMessage && error.backendMessage) {
        return error.backendMessage;
      }
      // Try resolving backend-specific error code first
      const backendCodeMessage = resolveBackendCodeMessage(error.backendCode, error.backendArgs);
      if (backendCodeMessage) return backendCodeMessage;
      // Otherwise use localized message, falling back to backend message if localization fails
      const localizedMessage = t(error.code, { defaultValue: '' });
      return localizedMessage || error.backendMessage || t('common:api.error.generic');
    }

    if (error instanceof Error) {
      return error.message;
    }

    return t('common:api.error.generic');
  };

  /**
   * Get error message with fallback translation key.
   * Use this in mutation error handlers to display proper ApiError codes or fallback messages.
   *
   * @param error - The error object from mutation
   * @param fallbackKey - i18n key to use when error is not an ApiError (e.g., 'authenticated.projects:toasts.createProjectError')
   * @param options - Options for error handling
   * @param options.preferBackendMessage - If true, prefer backend message over localized message for ApiError
   * @param options.interpolation - Interpolation values for the fallback key (e.g., { userName: 'John', projectName: 'Project A' })
   * @returns Translated error message
   *
   * @example
   * ```typescript
   * onError: error => {
   *   displayToastError(getErrorMessageWithFallback(error, 'authenticated.projects:toasts.createProjectError'));
   * }
   * ```
   */
  const getErrorMessageWithFallback = (
    error: unknown,
    fallbackKey: string,
    options?: { preferBackendMessage?: boolean; interpolation?: Record<string, string> }
  ): string => {
    const { preferBackendMessage = false, interpolation } = options ?? {};

    if (error instanceof ApiError) {
      if (preferBackendMessage && error.backendMessage) {
        return error.backendMessage;
      }
      // Try resolving backend-specific error code first
      const backendCodeMessage = resolveBackendCodeMessage(error.backendCode, error.backendArgs);
      if (backendCodeMessage) return backendCodeMessage;
      const localized = t(error.code, { defaultValue: '' });
      return localized || t(fallbackKey, interpolation);
    }

    return t(fallbackKey, interpolation);
  };

  /**
   * Display toast error with proper ApiError code or fallback message.
   * Convenience method that combines getErrorMessageWithFallback with displayToastError.
   *
   * @param error - The error object from mutation
   * @param fallbackKey - i18n key to use when error is not an ApiError (e.g., 'authenticated.projects:toasts.createProjectError')
   * @param options - Options for error handling
   * @param options.preferBackendMessage - If true, prefer backend message over localized message for ApiError
   * @param options.interpolation - Interpolation values for the fallback key (e.g., { userName: 'John', projectName: 'Project A' })
   *
   * @example
   * ```typescript
   * onError: error => {
   *   displayToastErrorFromApiWithFallback(error, 'authenticated.projects:toasts.createProjectError');
   * }
   * ```
   */
  const displayToastErrorFromApiWithFallback = (
    error: unknown,
    fallbackKey: string,
    options?: { preferBackendMessage?: boolean; interpolation?: Record<string, string> }
  ): void => {
    displayToastError(getErrorMessageWithFallback(error, fallbackKey, options));
  };

  return { getErrorMessage, getErrorMessageWithFallback, displayToastErrorFromApiWithFallback };
};
